import { importHighcharts } from '@/lib/highcharts'
import { BlockModule } from '../common'
import { doIfInViewOrDefer, chartLineColors, baseLineChartConfig } from './common'
import {
    getMarketDataGovernmentSecurityGovernmentInvestmentIssues,
    getMarketDataGovernmentSecurityMalaysianGovernmentSecurities,
} from '@/api'
import { defineComponent } from 'vue'
import ChartShareButtons from '@/components/website/ChartShareButtons.vue'
import type { InvestmentIssuesData } from '@/types'
import type { SeriesLineOptions } from 'highcharts'

new BlockModule({
    selector: '.block-market-data-government-security-government-investment-issues',
    vueComponent: defineComponent({
        components: { ChartShareButtons },
        setup() {},
    }),
    async setupBlock(el, onTeardown, blockContext) {
        const chartData = await getMarketDataGovernmentSecurityGovernmentInvestmentIssues()

        const timestamp = chartData.lastUpdated
        const date = new Date(timestamp)
        const lastUpdatedValue = date
            .toLocaleDateString('en-US', {
                day: 'numeric',
                month: 'short',
                year: 'numeric',
            })
            .replace(',', '')

        const setupCounters = () => {
            const lastUpdatedEl = el.querySelector('.last-updated')
            if (lastUpdatedEl && timestamp) {
                lastUpdatedEl.textContent = `Last updated: ${lastUpdatedValue}`
            }
        }

        const setupChart = async () => {
            const chartEl = el.querySelector('.chart-container')
            if (!chartEl) return

            const Highcharts = await importHighcharts()

            const seriesData = [
                { name: '3 Years', data: chartData.threeYears },
                { name: '5 Years', data: chartData.fiveYears },
                { name: '7 Years', data: chartData.sevenYears },
                { name: '10 Years', data: chartData.tenYears },
            ]

            const series: SeriesLineOptions[] = seriesData.map((series, index) => ({
                name: series.name,
                type: 'line',
                color: chartLineColors[index],
                data: series.data.map((item: InvestmentIssuesData) => ({
                    x: new Date(item.trading_date).getTime(),
                    y: parseFloat(item.tra_yie_close),
                })),
            }))

            const chart = Highcharts.chart({
                ...baseLineChartConfig,
                chart: {
                    ...baseLineChartConfig.chart,
                    renderTo: chartEl as HTMLElement,
                },
                xAxis: {
                    type: 'datetime',
                    lineColor: '#DBDBDB',
                    allowDecimals: false,
                },
                series,
                exporting: {
                    enabled: false,
                    filename: 'government-security-government-investment-issues',
                },
                tooltip: {
                    valueDecimals: 2,
                },
                yAxis: {
                    ...baseLineChartConfig.yAxis,
                    title: {
                        text: 'Yield (%)',
                        align: 'high',
                        rotation: 0,
                        reserveSpace: false,
                        textAlign: 'left',
                        y: -20,
                    },
                },
            })

            const download = el.querySelector<HTMLButtonElement>('.download-btn')
            if (download) {
                download.addEventListener('click', () => {
                    chart.downloadCSV()
                })
            }

            onTeardown(() => {
                chart.destroy()
            })
        }

        doIfInViewOrDefer(() => {
            setupChart()
            setupCounters()
        }, blockContext)
    },
})
